import React, { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { FaTimes } from "react-icons/fa";
import "./ReferFriendsModal.scss";

interface ReferFriendsModalProps {
  show: boolean;
  onHide: () => void;
  referralCode: string;
  onSendInvites: (emails: string[]) => Promise<void>;
}

const ReferFriendsModal: React.FC<ReferFriendsModalProps> = ({
  show,
  onHide,
  referralCode,
  onSendInvites,
}) => {
  const [emails, setEmails] = useState<string[]>([""]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: number]: string }>({});

  const handleAddAnother = () => {
    setEmails([...emails, ""]);
  };

  const handleEmailChange = (index: number, value: string) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);

    // Clear error for this field
    if (errors[index]) {
      const newErrors = { ...errors };
      delete newErrors[index];
      setErrors(newErrors);
    }
  };

  const handleRemoveEmail = (index: number) => {
    if (emails.length > 1) {
      const newEmails = emails.filter((_, i) => i !== index);
      setEmails(newEmails);

      // Adjust errors
      const newErrors: { [key: number]: string } = {};
      Object.keys(errors).forEach((key) => {
        const numKey = parseInt(key);
        if (numKey < index) {
          newErrors[numKey] = errors[numKey];
        } else if (numKey > index) {
          newErrors[numKey - 1] = errors[numKey];
        }
      });
      setErrors(newErrors);
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSendInvites = async () => {
    // Validate all emails
    const newErrors: { [key: number]: string } = {};
    let hasError = false;

    emails.forEach((email, index) => {
      if (!email.trim()) {
        newErrors[index] = "Email is required";
        hasError = true;
      } else if (!validateEmail(email.trim())) {
        newErrors[index] = "Invalid email format";
        hasError = true;
      }
    });

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    // Filter out empty emails and get unique emails
    const validEmails = [...new Set(emails.filter((e) => e.trim()).map((e) => e.trim()))];

    if (validEmails.length === 0) {
      return;
    }

    setLoading(true);
    try {
      await onSendInvites(validEmails);
      // Reset form on success
      setEmails([""]);
      setErrors({});
      onHide();
    } catch (error) {
      console.error("Error sending invites:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmails([""]);
    setErrors({});
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered className="refer-friends-modal">
      <Modal.Header className="pb-1">
        <Modal.Title style={{fontSize:'24px', color:'#3957A0', lineHeight:'100%'}}>Refer Friends<br/>
          <label style={{fontSize:'16px', fontWeight:400,color:'#677289'}}>Referral Code: <span style={{fontWeight:700}}>{referralCode}</span></label>
      </Modal.Title>
        
        <button className="close-btn" onClick={handleClose}>
          <FaTimes />
        </button>
      </Modal.Header>
      <Modal.Body className="p-3">
        

        <div className="email-inputs-section">
          <label className="email-label">Email*</label>
          {emails.map((email, index) => (
            <div key={index} className="email-input-wrapper mb-0">
              <Form.Control
                type="email"
                placeholder="Enter Email Address"
                value={email}
                onChange={(e) => handleEmailChange(index, e.target.value)}
                isInvalid={!!errors[index]}
                className="email-input"
              />
              {emails.length > 1 && (
                <button
                  type="button"
                  className="remove-email-btn"
                  onClick={() => handleRemoveEmail(index)}
                  aria-label="Remove email"
                >
                  <FaTimes />
                </button>
              )}
              {errors[index] && (
                <Form.Control.Feedback type="invalid">
                  {errors[index]}
                </Form.Control.Feedback>
              )}
            </div>
          ))}

          <button type="button" className="add-another-btn" onClick={handleAddAnother} style={{fontSize:'16px', color:'#3957A0'}}>
            + Add another
          </button>
        </div>

        <Button
          className="send-invites-btn"
          onClick={handleSendInvites}
          disabled={loading}
        >
          {loading ? "Sending..." : "Send Invites"}
        </Button>
      </Modal.Body>
    </Modal>
  );
};

export default ReferFriendsModal;
